"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Sticky "On this page" bar under the site header. Horizontal chips that scroll
 * on mobile; the chip for the section in view is highlighted.
 */
export default function OnThisPage({
  label,
  items,
}: {
  label: string;
  items: ReadonlyArray<{ id: string; label: string }>;
}) {
  const [active, setActive] = useState<string>(items[0]?.id ?? "");

  useEffect(() => {
    const els = items.map((i) => document.getElementById(i.id)).filter(Boolean) as HTMLElement[];
    if (!els.length || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-140px 0px -55% 0px" },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    // Keep the active chip visible by scrolling the chip row only. scrollIntoView
    // would also scroll the window, which jerks the page while reading.
    const chip = document.getElementById(`otp-${active}`);
    const row = chip?.closest("ol");
    if (!chip || !row) return;
    const left = chip.offsetLeft - row.clientWidth / 2 + chip.clientWidth / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [active]);

  return (
    <nav
      aria-label={label}
      data-agk-location="on-this-page"
      className="sticky top-16 z-40 border-y border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
        <span className="hidden shrink-0 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-muted lg:block">{label}</span>
        <ol className="relative flex flex-1 gap-1 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {items.map((item) => (
            <li key={item.id} className="shrink-0">
              <a
                id={`otp-${item.id}`}
                href={`#${item.id}`}
                aria-current={active === item.id ? "location" : undefined}
                className={cn(
                  "inline-flex min-h-9 items-center rounded-full px-3 text-sm transition-colors",
                  active === item.id
                    ? "bg-primary text-primary-foreground"
                    : "text-neutral-muted hover:bg-neutral-surface hover:text-neutral-emphasis",
                )}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}
